import {
	Box,
	Button,
	Card,
	CardActions,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { Fragment, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
	useEditHealthMutation,
	useGetAdmissionRequestDetailQuery,
} from '@/api/member-management/admissionRequest';
import { EditHealthRequest } from '@/api/DTO/member-management/admissionRequest';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import {
	useGetAllEnumListQuery,
	useGetDiagnosisTreeListQuery,
	useGetDicListQuery,
} from '@/api/common';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import AppAutocompleteCheckbox from '@/common/components/form-inputs/AppAutocompleteCheckbox';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';

interface Props {
	type: string;
}
export default function EditHealthInformation(props: Props) {
	const { type } = props;
	const memberType = i18n.t(
		type === 'admissionRequest'
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { admissionRequestId } = useParams();
	const [editHealth] = useEditHealthMutation();

	const { data: followUpClinicPsychiatryDicList } = useGetDicListQuery(
		'follow_up_clinic_psychiatry',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: mentalHealthConditionDicList } = useGetDicListQuery(
		'mental_health_condition',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: suspectedMentalHealthProblemDicList } = useGetDicListQuery(
		'suspected_mental_health_problem',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: addictionProblemTypeDicList } = useGetDicListQuery(
		'addiction_problem_type',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: coreConcernDicList } = useGetDicListQuery('core_concern', {
		refetchOnMountOrArgChange: true,
	});
	const { data: diagnosisTreeList } = useGetDiagnosisTreeListQuery(null, {
		refetchOnMountOrArgChange: true,
	});
	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: details } = useGetAdmissionRequestDetailQuery(
		admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const defaultValues: DefaultValues<EditHealthRequest> = {
		diagnosisStatus: null,
	};

	const formSchema = Yup.object().shape({
		// abuseRole: Yup.string().required(t('errorMessage.pleaseSelect')),
		// abuseTypeList: Yup.array()
		// 	.of(Yup.string().required())
		// 	.min(1, t('errorMessage.pleaseSelect'))
		// 	.required(t('errorMessage.pleaseSelect'))
		// 	.nullable(),

		abuseRole: Yup.string().when('abuseHistory', {
			is: (val: number) => val === 0,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),

		abuseTypeList: Yup.array().when('abuseHistory', {
			is: (val: number) => val === 0,
			then: (schema) =>
				schema
					.of(Yup.string().required())
					.min(1, t('errorMessage.pleaseSelect'))
					.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		diagnosisStatus: Yup.string().required(t('errorMessage.pleaseSelect')),

		mainDiagnosis: Yup.string().when('diagnosisStatus', {
			is: (mainDiagnosis: string) => mainDiagnosis != '2',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),

		secondaryDiagnosis: Yup.string().when('diagnosisStatus', {
			is: (mainDiagnosis: string) => mainDiagnosis == '1',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),

		suspectedMentalHealthProblem: Yup.string().when('diagnosisStatus', {
			is: (mainDiagnosis: string) => mainDiagnosis == '2',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),

		mainDiagnosisSpecify: Yup.string().when('mainDiagnosis', {
			is: (mainDiagnosis: string) => mainDiagnosis && mainDiagnosisIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		secondaryDiagnosisSpecify: Yup.string().when('secondaryDiagnosis', {
			is: (secondaryDiagnosis: string) =>
				secondaryDiagnosis && secondaryDiagnosisIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		suspectedMentalHealthProblemSpecify: Yup.string().when(
			'suspectedMentalHealthProblem',
			{
				is: (suspectedMentalHealthProblem: string) =>
					suspectedMentalHealthProblem && suspectedMentalHealthProblemIsOther(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}
		),
		abuseTypeSpecify: Yup.string().when('abuseTypeList', {
			is: (abuseTypeList: string) => abuseTypeList && abuseTypeSpecifyIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		typeOfAddictionProblemSpecify: Yup.string().when(
			'typeOfAddictionProblemList',
			{
				is: (typeOfAddictionProblemList: string) =>
					typeOfAddictionProblemList && typeOfAddictionProblemSpecifyIsOther(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}
		),
		typeOfAddictionProblemList: Yup.array()
			.of(Yup.string())
			.when('addictionProblem', {
				is: (addictionProblem: string) => {
					return addictionProblemIsYes();
				},
				then: (schema) =>
					schema
						.required(t('errorMessage.pleaseSelect'))
						.min(1, t('errorMessage.pleaseSelect')),
				otherwise: (schema) => schema.nullable(),
			}),
		dateOfLinkUpToHealthSystem: Yup.string()
			.nullable()
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	const watchMainDiagnosis = methods.watch('mainDiagnosis');
	const watchSecondaryDiagnosis = methods.watch('secondaryDiagnosis');
	const watchSuspectedMentalHealthProblem = methods.watch(
		'suspectedMentalHealthProblem'
	);
	const watchAbuseTypeList = methods.watch('abuseTypeList');
	const watchTypeOfAddictionProblemList = methods.watch(
		'typeOfAddictionProblemList'
	);
	const watchAddictionProblem = methods.watch('addictionProblem');
	const watchDiagnosisStatus = methods.watch('diagnosisStatus');
	const watchAbuseHistory = methods.watch('abuseHistory');

	useEffect(() => {
		if (details) {
			methods.reset(details.data);
		}
	}, [details]);

	const mainDiagnosisIsOther = () => {
		const flatDiagnosisList = diagnosisTreeList?.data.flatMap((item: any) => [
			{ ...item },
			...(item.childrenList?.map((sub: any) => ({ ...sub })) || []),
		]);
		const obj = (flatDiagnosisList || []).find(
			(item: any) => item.id == watchMainDiagnosis
		);
		if (obj && obj.labelEn === 'Others') return true;
		return false;
	};
	const secondaryDiagnosisIsOther = () => {
		const flatDiagnosisList = diagnosisTreeList?.data.flatMap((item: any) => [
			{ ...item },
			...(item.childrenList?.map((sub: any) => ({ ...sub })) || []),
		]);
		const obj = (flatDiagnosisList || []).find(
			(item: any) => item.id == watchSecondaryDiagnosis
		);
		if (obj && obj.labelEn === 'Others') return true;
		return false;
	};
	const suspectedMentalHealthProblemIsOther = () => {
		const obj = suspectedMentalHealthProblemDicList?.data.find(
			(item: any) => item.id == watchSuspectedMentalHealthProblem
		);
		if (obj && obj.code == '8') return true;
		return false;
	};
	const abuseTypeSpecifyIsOther = () => {
		const obj = allEnumList?.data.AbuseTypeEnum.find((item: any) => {
			if (watchAbuseTypeList?.includes(item.code)) {
				if (item.desc == 'Others') return item;
			}
		});
		if (obj) return true;
		return false;
	};
	const addictionProblemIsYes = () => {
		const obj = allEnumList?.data.AddictionProblemEnum.find((item: any) => {
			if (item.code === watchAddictionProblem) return item;
		});
		if (obj && obj.desc == 'Yes') return true;
		return false;
	};
	const typeOfAddictionProblemSpecifyIsOther = () => {
		const obj = addictionProblemTypeDicList?.data.find((item: any) => {
			if (watchTypeOfAddictionProblemList?.includes(item.id)) {
				if (item.labelEn == 'Others') return item;
			}
		});
		if (obj) return true;
		return false;
	};

	const onSubmit: SubmitHandler<EditHealthRequest> = async (data) => {
		const obj: EditHealthRequest = { ...data };
		const response = await editHealth(obj);

		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateMemberSuccess', { type: memberType }), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				`/member-management/${
					type == 'member' ? 'member-list' : 'admission-request-list'
				}/detail/${admissionRequestId}`
			);
		}
	};

	const handleClose = () => {
		navigate(
			`/member-management/${
				type == 'member' ? 'member-list' : 'admission-request-list'
			}/detail/${admissionRequestId}`,
			{ replace: true }
		);
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)}>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h4'>
								{type == 'admissionRequest'
									? t('drawerListItems.editAdmissionRequest')
									: t('drawerListItems.editMember')}
							</Typography>
						</Box>
						<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
							<Box
								display='flex'
								justifyContent='space-between'
								marginBottom={2}
							>
								<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
									{t('preAdmission.mentalHealthInformation')}
								</Typography>
							</Box>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={6}>
									<AppAutoCompleteMenu
										optional={true}
										control={methods.control}
										name='followUpClinicPsychiatry'
										label={t('preAdmission.followUpClinicPsychiatry')}
										options={(followUpClinicPsychiatryDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppMultipleDropDownMenu
										control={methods.control}
										name='mentalHealthConditionList'
										label={t('preAdmission.mentalHealthConditionOther')}
										options={(mentalHealthConditionDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('member.diagnosisStatus') + '*'}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name={`diagnosisStatus`}
										row
										options={(allEnumList?.data.DiagnosisStatusEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('mainDiagnosis', '');
											methods.setValue('mainDiagnosisSpecify', '');
											methods.setValue('secondaryDiagnosis', '');
											methods.setValue('secondaryDiagnosisSpecify', '');
											methods.setValue('suspectedMentalHealthProblem', '');
											methods.setValue(
												'suspectedMentalHealthProblemSpecify',
												''
											);

											methods.trigger('mainDiagnosis');
											methods.trigger('secondaryDiagnosis');
											methods.trigger('suspectedMentalHealthProblem');
											methods.trigger('mainDiagnosisSpecify');
											methods.trigger('secondaryDiagnosisSpecify');
											methods.trigger('suspectedMentalHealthProblemSpecify');
										}}
									/>
								</Grid>
								{(watchDiagnosisStatus == '0' || watchDiagnosisStatus == 1) && (
									<>
										<Grid size={6}>
											<AppAutoCompleteMenu
												control={methods.control}
												name='mainDiagnosis'
												label={
													t('preAdmission.mainDiagnosis') +
													`${watchDiagnosisStatus != 2 ? '*' : ''}`
												}
												options={diagnosisTreeList?.data?.map((item: any) => {
													const childrenList = (item.childrenList || []).map(
														(child: any) => {
															return {
																...child,
																name: child.labelEn || '',
															};
														}
													);
													return {
														...item,
														childrenList: childrenList,
														name: item.labelEn || '',
													};
												})}
												handleChange={() => {
													methods.setValue('mainDiagnosisSpecify', '');
													methods.clearErrors('mainDiagnosisSpecify');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!mainDiagnosisIsOther()}
												control={methods.control}
												name='mainDiagnosisSpecify'
												label={`${t('preAdmission.mainDiagnosisSpecify')}${
													mainDiagnosisIsOther() ? '*' : ''
												}`}
											/>
										</Grid>
									</>
								)}
								{watchDiagnosisStatus == 1 && (
									<>
										<Grid size={6}>
											<AppAutoCompleteMenu
												optional={true}
												control={methods.control}
												name='secondaryDiagnosis'
												label={
													t('preAdmission.secondaryDiagnosis') +
													`${watchDiagnosisStatus == 1 ? '*' : ''}`
												}
												options={diagnosisTreeList?.data?.map((item: any) => {
													const childrenList = (item.childrenList || []).map(
														(child: any) => {
															return {
																...child,
																name: child.labelEn || '',
															};
														}
													);
													return {
														...item,
														childrenList: childrenList,
														name: item.labelEn || '',
													};
												})}
												handleChange={() => {
													methods.setValue('secondaryDiagnosisSpecify', '');
													methods.clearErrors('secondaryDiagnosisSpecify');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!secondaryDiagnosisIsOther()}
												control={methods.control}
												name='secondaryDiagnosisSpecify'
												label={`${t('preAdmission.secondaryDiagnosisSpecify')}${
													secondaryDiagnosisIsOther() ? '*' : ''
												}`}
											/>
										</Grid>
									</>
								)}

								{watchDiagnosisStatus == 2 && (
									<>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='suspectedMentalHealthProblem'
												label={
													t('preAdmission.suspectedMentalHealthProblem') +
													`${watchDiagnosisStatus == 2 ? '*' : ''}`
												}
												options={(
													suspectedMentalHealthProblemDicList?.data || []
												).map((item: { labelEn: any }) => {
													return {
														...item,
														name: item.labelEn,
													};
												})}
												handleChange={() => {
													methods.setValue(
														'suspectedMentalHealthProblemSpecify',
														''
													);
													methods.clearErrors(
														'suspectedMentalHealthProblemSpecify'
													);
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!suspectedMentalHealthProblemIsOther()}
												control={methods.control}
												name='suspectedMentalHealthProblemSpecify'
												label={`${t(
													'preAdmission.suspectedMentalHealthProblemSpecify'
												)}${suspectedMentalHealthProblemIsOther() ? '*' : ''}`}
											/>
										</Grid>
									</>
								)}

								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='suicideIdeation'
										label={t('preAdmission.suicideIdeation')}
										options={(allEnumList?.data.SuicideIdeationEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='suicideHistory'
										label={t('preAdmission.suicideHistory')}
										options={(allEnumList?.data.SuicideHistoryEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='violenceIdeation'
										label={t('preAdmission.violenceIdeation')}
										options={(allEnumList?.data.ViolenceIdeationEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='violenceHistory'
										label={t('preAdmission.violenceHistory')}
										options={(allEnumList?.data.ViolenceHistoryEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='abuseHistory'
										label={t('preAdmission.abuseHistory')}
										options={(allEnumList?.data.AbuseHistoryEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('abuseRole', '');
											methods.setValue('abuseTypeList', []);

											methods.clearErrors('abuseRole');
											methods.clearErrors('abuseTypeList');
										}}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='abuseRole'
										label={`${t('preAdmission.abuseRole')}${
											watchAbuseHistory === 0 ? '*' : ''
										}`}
										options={(allEnumList?.data.AbuseRoleEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppMultipleDropDownMenu
										control={methods.control}
										name='abuseTypeList'
										label={`${t('preAdmission.abuseType')}${
											watchAbuseHistory === 0 ? '*' : ''
										}`}
										options={(allEnumList?.data.AbuseTypeEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('abuseTypeSpecify', '');
											methods.clearErrors('abuseTypeSpecify');
										}}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										disabled={!abuseTypeSpecifyIsOther()}
										control={methods.control}
										name='abuseTypeSpecify'
										label={`${t('preAdmission.abuseTypeSpecify')}${
											abuseTypeSpecifyIsOther() ? '*' : ''
										}`}
									/>
								</Grid>

								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='addictionProblem'
										label={t('preAdmission.addictionProblem')}
										options={(allEnumList?.data.AddictionProblemEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('typeOfAddictionProblemSpecify', '');
											methods.setValue('typeOfAddictionProblemList', []);
											methods.clearErrors('typeOfAddictionProblemSpecify');
											methods.clearErrors('typeOfAddictionProblemList');
										}}
									/>
								</Grid>
								<Grid size={6}></Grid>
								<Grid size={6}>
									<AppMultipleDropDownMenu
										disabled={!addictionProblemIsYes()}
										control={methods.control}
										name='typeOfAddictionProblemList'
										label={`${t('preAdmission.typeAddictionProblem')}${
											addictionProblemIsYes() ? '*' : ''
										}`}
										options={(addictionProblemTypeDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('typeOfAddictionProblemSpecify', '');
											methods.clearErrors('typeOfAddictionProblemSpecify');
										}}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='typeOfAddictionProblemSpecify'
										disabled={!typeOfAddictionProblemSpecifyIsOther()}
										label={`${t('preAdmission.typeAddictionProblemSpecify')}${
											typeOfAddictionProblemSpecifyIsOther() ? '*' : ''
										}`}
									/>
								</Grid>
								<Grid size={6}>
									<AppAutocompleteCheckbox
										control={methods.control}
										name='coreConcernList'
										label={t('preAdmission.coreConcern')}
										options={(coreConcernDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppDatePicker
										maximumDate={dayjs()
											.startOf('day')
											.format(API_DATE_FORMAT)}
										control={methods.control}
										name='dateOfLinkUpToHealthSystem'
										label={t('preAdmission.dateLinkUpHealthSystem')}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='familyMembersHaveHistoryOfMentalIllness'
										label={t('preAdmission.familyMembersHaveHistoryMI')}
										options={(
											allEnumList?.data.FamilyMembersHaveHistoryOfMIEnum || []
										).map((item: any) => {
											return {
												...item,
												id: item.code,
												name: item.desc,
											};
										})}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='otherIllnessDisability'
										label={t('preAdmission.OtherIllnessDisability')}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='enquiryRecordRemarks'
										label={t('preAdmission.remarks(EnquiryRecord)')}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='membershipApplicationRemarks'
										label={t('preAdmission.remarks(MembershipApplication)')}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='faceSheetRemarks'
										label={t('preAdmission.remarks(FaceSheet)')}
									/>
								</Grid>
							</Grid>
							<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
								<Button onClick={handleClose}>{t('button.cancel')}</Button>
								<Button
									type='submit'
									variant='contained'
									endIcon={<CheckCircleOutlineIcon />}
								>
									{t('button.complete')}
								</Button>
							</CardActions>
						</Card>
					</form>
				</FormProvider>
			</Box>
		</>
	);
}
